/**
 * Teams Domain Seed Data
 *
 * Contains all seed data for API Gateway team features:
 * - Teams (organizational units within businesses)
 * - Team Memberships
 *
 * Schema location: 2-api-gateway/prisma/schema.prisma (lines 845-887)
 *
 * @see PR #237 - User/Employee Management
 */

import { seedId } from '../utils';
import { BUSINESS_IDS } from './tenants';
import { USER_IDS } from './users';

// =============================================================================
// TEAM IDs
// =============================================================================

export const TEAM_IDS = {
  // OpsMatrix Capital teams (6 teams)
  FUNDING_SALES: seedId('team-funding-sales', 1),
  FUNDING_UNDERWRITING: seedId('team-funding-uw', 2),
  FUNDING_CLOSING: seedId('team-funding-closing', 3),
  FUNDING_SERVICING: seedId('team-funding-servicing', 4),
  FUNDING_SUPPORT: seedId('team-funding-support', 5),
  FUNDING_EXECUTIVE: seedId('team-funding-exec', 6),
  FUNDING_DRAW_INSPECTION: seedId('team-funding-draw', 7),
  FUNDING_COMPLIANCE: seedId('team-funding-compliance', 8),

  // 1Sharpe Capital teams (5 teams)
  SHARPE_ORIGINATIONS: seedId('team-sharpe-orig', 1),
  SHARPE_CONSTRUCTION: seedId('team-sharpe-construction', 2),
  SHARPE_OPERATIONS: seedId('team-sharpe-ops', 3),
  SHARPE_PORTFOLIO: seedId('team-sharpe-portfolio', 4),
  SHARPE_RISK: seedId('team-sharpe-risk', 5),

  // Pacific Bridge Fund teams (3 teams)
  PACIFIC_INVESTMENT: seedId('team-pacific-invest', 1),
  PACIFIC_ACQUISITIONS: seedId('team-pacific-acq', 2),
  PACIFIC_ASSET_MGMT: seedId('team-pacific-am', 3),

  // Gulf Coast Family Office teams (2 teams)
  GULF_ADVISORY: seedId('team-gulf-advisory', 1),
  GULF_INVESTMENTS: seedId('team-gulf-investments', 2),
} as const;

export type TeamId = (typeof TEAM_IDS)[keyof typeof TEAM_IDS];

// =============================================================================
// TEAM MEMBERSHIP IDs
// =============================================================================

export const TEAM_MEMBERSHIP_IDS = {
  // ==========================================================================
  // OpsMatrix Capital - Sales Team (4 members)
  // ==========================================================================
  RACHEL_SALES_LEAD: seedId('membership-rachel-sales', 1),
  JAMES_SALES_MEMBER: seedId('membership-james-sales', 1),
  JOHN_SALES_MEMBER: seedId('membership-john-sales', 2),
  MARIA_SALES_MEMBER: seedId('membership-maria-sales', 3),

  // ==========================================================================
  // OpsMatrix Capital - Underwriting Team (5 members)
  // ==========================================================================
  SARAH_C_UW_LEAD: seedId('membership-sarahc-uw', 1),
  SARAH_W_UW_MEMBER: seedId('membership-sarahw-uw', 2),
  DAVID_UW_MEMBER: seedId('membership-david-uw', 3),
  ALEX_UW_MEMBER: seedId('membership-alex-uw', 4),
  OPS_MGR_UW_MEMBER: seedId('membership-opsmgr-uw', 5),

  // ==========================================================================
  // OpsMatrix Capital - Closing Team (4 members)
  // ==========================================================================
  MIKE_J_CLOSING_LEAD: seedId('membership-mikej-closing', 1),
  MIKE_C_CLOSING_MEMBER: seedId('membership-mikec-closing', 2),
  EMILY_N_CLOSING_MEMBER: seedId('membership-emilyn-closing', 3),
  JOHN_CLOSING_MEMBER: seedId('membership-john-closing', 4),

  // ==========================================================================
  // OpsMatrix Capital - Servicing Team (3 members)
  // ==========================================================================
  EMILY_D_SERVICING_LEAD: seedId('membership-emilyd-servicing', 1),
  SARAH_W_SERVICING_MEMBER: seedId('membership-sarahw-servicing', 2),
  DAVID_SERVICING_MEMBER: seedId('membership-david-servicing', 3),

  // ==========================================================================
  // OpsMatrix Capital - Support Team (2 members)
  // ==========================================================================
  EMILY_D_SUPPORT_LEAD: seedId('membership-emilyd-support', 1),
  JAMES_SUPPORT_MEMBER: seedId('membership-james-support', 2),

  // ==========================================================================
  // OpsMatrix Capital - Draw Inspection Team (2 members)
  // ==========================================================================
  JAMES_DRAW_LEAD: seedId('membership-james-draw', 1),
  MIKE_C_DRAW_MEMBER: seedId('membership-mikec-draw', 2),

  // ==========================================================================
  // OpsMatrix Capital - Compliance Team (3 members)
  // ==========================================================================
  MARIA_COMPLIANCE_LEAD: seedId('membership-maria-compliance', 1),
  DAVID_COMPLIANCE_MEMBER: seedId('membership-david-compliance', 2),
  ALEX_COMPLIANCE_MEMBER: seedId('membership-alex-compliance', 3),

  // ==========================================================================
  // OpsMatrix Capital - Executive Team (3 members)
  // ==========================================================================
  RACHEL_EXEC_LEAD: seedId('membership-rachel-exec', 1),
  OPS_MGR_EXEC_MEMBER: seedId('membership-opsmgr-exec', 2),
  MARIA_EXEC_MEMBER: seedId('membership-maria-exec', 3),

  // ==========================================================================
  // 1Sharpe Capital - Originations Team (2 members)
  // ==========================================================================
  SHARPE_ADMIN_ORIG_LEAD: seedId('membership-sharpeadmin-orig', 1),
  ANALYST_ORIG_MEMBER: seedId('membership-analyst-orig', 2),

  // ==========================================================================
  // 1Sharpe Capital - Portfolio Team (2 members)
  // ==========================================================================
  PORTFOLIO_MGR_LEAD: seedId('membership-portfoliomgr', 1),
  ANALYST_PORTFOLIO_MEMBER: seedId('membership-analyst-portfolio', 2),

  // ==========================================================================
  // 1Sharpe Capital - Risk Team (2 members)
  // ==========================================================================
  SHARPE_ADMIN_RISK_LEAD: seedId('membership-sharpeadmin-risk', 1),
  PORTFOLIO_MGR_RISK_MEMBER: seedId('membership-portfoliomgr-risk', 2),

  // ==========================================================================
  // Pacific Bridge Fund - Investment Committee (2 members)
  // ==========================================================================
  FUND_MGR_INVEST_LEAD: seedId('membership-fundmgr-invest', 1),
  PACIFIC_ADMIN_INVEST_MEMBER: seedId('membership-pacificadmin-invest', 2),

  // ==========================================================================
  // Pacific Bridge Fund - Acquisitions (1 member)
  // ==========================================================================
  FUND_MGR_ACQ_LEAD: seedId('membership-fundmgr-acq', 1),

  // ==========================================================================
  // Gulf Coast Family Office - Advisory (1 member)
  // ==========================================================================
  PRINCIPAL_ADVISORY_LEAD: seedId('membership-principal-advisory', 1),

  // ==========================================================================
  // Gulf Coast Family Office - Investments (1 member)
  // ==========================================================================
  PRINCIPAL_INVESTMENTS_LEAD: seedId('membership-principal-investments', 1),
} as const;

export type TeamMembershipId = (typeof TEAM_MEMBERSHIP_IDS)[keyof typeof TEAM_MEMBERSHIP_IDS];

// =============================================================================
// DATA TYPES
// =============================================================================

export interface TeamSeedData {
  id: string;
  businessId: string;
  name: string;
  description?: string;
  color?: string; // Hex color for UI (e.g., "#4CAF50")
  icon?: string; // Icon name for UI (e.g., "engineering", "sales")
  managerId?: string; // User ID of team manager
  isActive: boolean;
  createdBy: string; // User ID who created the team
}

export interface TeamMembershipSeedData {
  id: string;
  teamId: string;
  userId: string;
  role: 'member' | 'lead'; // Per API Gateway schema
  joinedAt: string; // ISO8601 timestamp
}

// =============================================================================
// TEAM DATA
// =============================================================================

export const TEAMS: Record<keyof typeof TEAM_IDS, TeamSeedData> = {
  // ==========================================================================
  // OPSMATRIX CAPITAL TEAMS
  // ==========================================================================
  FUNDING_SALES: {
    id: TEAM_IDS.FUNDING_SALES,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Sales Team',
    description: 'Outbound sales, lead qualification, and new business development',
    color: '#4CAF50', // Green
    icon: 'sales',
    managerId: USER_IDS.RACHEL_MARTINEZ,
    isActive: true,
    createdBy: USER_IDS.RACHEL_MARTINEZ,
  },
  FUNDING_UNDERWRITING: {
    id: TEAM_IDS.FUNDING_UNDERWRITING,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Underwriting Team',
    description: 'Loan underwriting, risk assessment, and credit analysis',
    color: '#2196F3', // Blue
    icon: 'analytics',
    managerId: USER_IDS.MARIA_RODRIGUEZ,
    isActive: true,
    createdBy: USER_IDS.RACHEL_MARTINEZ,
  },
  FUNDING_CLOSING: {
    id: TEAM_IDS.FUNDING_CLOSING,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Closing Team',
    description: 'Loan closing, documentation, and funding coordination',
    color: '#9C27B0', // Purple
    icon: 'document',
    isActive: true,
    createdBy: USER_IDS.RACHEL_MARTINEZ,
  },
  FUNDING_SERVICING: {
    id: TEAM_IDS.FUNDING_SERVICING,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Servicing Team',
    description: 'Post-close loan servicing, payment processing, and borrower support',
    color: '#FF9800', // Orange
    icon: 'payment',
    isActive: true,
    createdBy: USER_IDS.RACHEL_MARTINEZ,
  },
  FUNDING_SUPPORT: {
    id: TEAM_IDS.FUNDING_SUPPORT,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Customer Support',
    description: 'Borrower and partner support, case management',
    color: '#00BCD4', // Cyan
    icon: 'support',
    managerId: USER_IDS.EMILY_DAVIS,
    isActive: true,
    createdBy: USER_IDS.RACHEL_MARTINEZ,
  },
  FUNDING_EXECUTIVE: {
    id: TEAM_IDS.FUNDING_EXECUTIVE,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Executive Team',
    description: 'C-suite and senior leadership',
    color: '#607D8B', // Blue Grey
    icon: 'executive',
    isActive: true,
    createdBy: USER_IDS.RACHEL_MARTINEZ,
  },
  FUNDING_DRAW_INSPECTION: {
    id: TEAM_IDS.FUNDING_DRAW_INSPECTION,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Draw Inspection',
    description: 'Construction draw management and site inspections',
    color: '#8D6E63', // Brown
    icon: 'construction',
    managerId: USER_IDS.JAMES_WILSON,
    isActive: true,
    createdBy: USER_IDS.RACHEL_MARTINEZ,
  },
  FUNDING_COMPLIANCE: {
    id: TEAM_IDS.FUNDING_COMPLIANCE,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Compliance',
    description: 'Regulatory compliance, audits, and policy enforcement',
    color: '#F44336', // Red
    icon: 'policy',
    managerId: USER_IDS.MARIA_RODRIGUEZ,
    isActive: true,
    createdBy: USER_IDS.RACHEL_MARTINEZ,
  },

  // ==========================================================================
  // 1SHARPE CAPITAL TEAMS
  // ==========================================================================
  SHARPE_ORIGINATIONS: {
    id: TEAM_IDS.SHARPE_ORIGINATIONS,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    name: 'Originations',
    description: 'Loan origination and sales',
    color: '#3F51B5', // Indigo
    icon: 'originations',
    managerId: USER_IDS.ALEX_KUMAR,
    isActive: true,
    createdBy: USER_IDS.ALEX_KUMAR,
  },
  SHARPE_CONSTRUCTION: {
    id: TEAM_IDS.SHARPE_CONSTRUCTION,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    name: 'Construction Lending',
    description: 'Specialized construction and ground-up lending team',
    color: '#795548', // Brown
    icon: 'construction',
    isActive: true,
    createdBy: USER_IDS.ALEX_KUMAR,
  },
  SHARPE_OPERATIONS: {
    id: TEAM_IDS.SHARPE_OPERATIONS,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    name: 'Operations',
    description: 'Back-office operations and administration',
    color: '#9E9E9E', // Grey
    icon: 'operations',
    isActive: true,
    createdBy: USER_IDS.SHARPE_ADMIN,
  },
  SHARPE_PORTFOLIO: {
    id: TEAM_IDS.SHARPE_PORTFOLIO,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    name: 'Portfolio Management',
    description: 'Active portfolio monitoring and performance tracking',
    color: '#673AB7', // Deep Purple
    icon: 'portfolio',
    managerId: USER_IDS.PORTFOLIO_MANAGER,
    isActive: true,
    createdBy: USER_IDS.SHARPE_ADMIN,
  },
  SHARPE_RISK: {
    id: TEAM_IDS.SHARPE_RISK,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    name: 'Risk Management',
    description: 'Credit risk assessment and portfolio risk analysis',
    color: '#FF5722', // Deep Orange
    icon: 'risk',
    managerId: USER_IDS.SHARPE_ADMIN,
    isActive: true,
    createdBy: USER_IDS.SHARPE_ADMIN,
  },

  // ==========================================================================
  // PACIFIC BRIDGE FUND TEAMS
  // ==========================================================================
  PACIFIC_INVESTMENT: {
    id: TEAM_IDS.PACIFIC_INVESTMENT,
    businessId: BUSINESS_IDS.PACIFIC_BRIDGE_FUND,
    name: 'Investment Committee',
    description: 'Loan approval and investment decisions',
    color: '#E91E63', // Pink
    icon: 'investment',
    managerId: USER_IDS.FUND_MANAGER,
    isActive: true,
    createdBy: USER_IDS.PACIFIC_ADMIN,
  },
  PACIFIC_ACQUISITIONS: {
    id: TEAM_IDS.PACIFIC_ACQUISITIONS,
    businessId: BUSINESS_IDS.PACIFIC_BRIDGE_FUND,
    name: 'Acquisitions',
    description: 'Deal sourcing and new investment opportunities',
    color: '#009688', // Teal
    icon: 'deals',
    managerId: USER_IDS.FUND_MANAGER,
    isActive: true,
    createdBy: USER_IDS.PACIFIC_ADMIN,
  },
  PACIFIC_ASSET_MGMT: {
    id: TEAM_IDS.PACIFIC_ASSET_MGMT,
    businessId: BUSINESS_IDS.PACIFIC_BRIDGE_FUND,
    name: 'Asset Management',
    description: 'Post-investment asset monitoring and value creation',
    color: '#00ACC1', // Cyan
    icon: 'assets',
    isActive: true,
    createdBy: USER_IDS.PACIFIC_ADMIN,
  },

  // ==========================================================================
  // GULF COAST FAMILY OFFICE TEAMS
  // ==========================================================================
  GULF_ADVISORY: {
    id: TEAM_IDS.GULF_ADVISORY,
    businessId: BUSINESS_IDS.GULF_COAST_FO,
    name: 'Family Advisory',
    description: 'Wealth planning and family governance',
    color: '#1E88E5', // Blue
    icon: 'family',
    managerId: USER_IDS.PRINCIPAL,
    isActive: true,
    createdBy: USER_IDS.PRINCIPAL,
  },
  GULF_INVESTMENTS: {
    id: TEAM_IDS.GULF_INVESTMENTS,
    businessId: BUSINESS_IDS.GULF_COAST_FO,
    name: 'Direct Investments',
    description: 'Real estate and private credit investments',
    color: '#43A047', // Green
    icon: 'investment',
    managerId: USER_IDS.PRINCIPAL,
    isActive: true,
    createdBy: USER_IDS.PRINCIPAL,
  },
};

// =============================================================================
// TEAM MEMBERSHIP DATA
// =============================================================================

export const TEAM_MEMBERSHIPS: Record<keyof typeof TEAM_MEMBERSHIP_IDS, TeamMembershipSeedData> = {
  // ==========================================================================
  // OpsMatrix Capital - Sales Team (4 members)
  // ==========================================================================
  RACHEL_SALES_LEAD: {
    id: TEAM_MEMBERSHIP_IDS.RACHEL_SALES_LEAD,
    teamId: TEAM_IDS.FUNDING_SALES,
    userId: USER_IDS.RACHEL_MARTINEZ,
    role: 'lead',
    joinedAt: '2023-01-15T09:00:00Z',
  },
  JAMES_SALES_MEMBER: {
    id: TEAM_MEMBERSHIP_IDS.JAMES_SALES_MEMBER,
    teamId: TEAM_IDS.FUNDING_SALES,
    userId: USER_IDS.JAMES_WILSON,
    role: 'member',
    joinedAt: '2023-06-01T10:00:00Z',
  },
  JOHN_SALES_MEMBER: {
    id: TEAM_MEMBERSHIP_IDS.JOHN_SALES_MEMBER,
    teamId: TEAM_IDS.FUNDING_SALES,
    userId: USER_IDS.JOHN_SMITH,
    role: 'member',
    joinedAt: '2024-03-01T09:00:00Z',
  },
  MARIA_SALES_MEMBER: {
    id: TEAM_MEMBERSHIP_IDS.MARIA_SALES_MEMBER,
    teamId: TEAM_IDS.FUNDING_SALES,
    userId: USER_IDS.MARIA_RODRIGUEZ,
    role: 'member',
    joinedAt: '2024-01-15T09:00:00Z',
  },

  // ==========================================================================
  // OpsMatrix Capital - Underwriting Team (5 members)
  // ==========================================================================
  SARAH_C_UW_LEAD: {
    id: TEAM_MEMBERSHIP_IDS.SARAH_C_UW_LEAD,
    teamId: TEAM_IDS.FUNDING_UNDERWRITING,
    userId: USER_IDS.SARAH_CHEN,
    role: 'lead',
    joinedAt: '2023-02-01T09:00:00Z',
  },
  SARAH_W_UW_MEMBER: {
    id: TEAM_MEMBERSHIP_IDS.SARAH_W_UW_MEMBER,
    teamId: TEAM_IDS.FUNDING_UNDERWRITING,
    userId: USER_IDS.SARAH_WILLIAMS,
    role: 'member',
    joinedAt: '2023-08-15T09:00:00Z',
  },
  DAVID_UW_MEMBER: {
    id: TEAM_MEMBERSHIP_IDS.DAVID_UW_MEMBER,
    teamId: TEAM_IDS.FUNDING_UNDERWRITING,
    userId: USER_IDS.DAVID_PARK,
    role: 'member',
    joinedAt: '2023-09-01T09:00:00Z',
  },
  ALEX_UW_MEMBER: {
    id: TEAM_MEMBERSHIP_IDS.ALEX_UW_MEMBER,
    teamId: TEAM_IDS.FUNDING_UNDERWRITING,
    userId: USER_IDS.ALEX_KUMAR,
    role: 'member',
    joinedAt: '2024-02-01T09:00:00Z',
  },
  OPS_MGR_UW_MEMBER: {
    id: TEAM_MEMBERSHIP_IDS.OPS_MGR_UW_MEMBER,
    teamId: TEAM_IDS.FUNDING_UNDERWRITING,
    userId: USER_IDS.OPS_MANAGER,
    role: 'member',
    joinedAt: '2023-03-01T09:00:00Z',
  },

  // ==========================================================================
  // OpsMatrix Capital - Closing Team (4 members)
  // ==========================================================================
  MIKE_J_CLOSING_LEAD: {
    id: TEAM_MEMBERSHIP_IDS.MIKE_J_CLOSING_LEAD,
    teamId: TEAM_IDS.FUNDING_CLOSING,
    userId: USER_IDS.MIKE_JOHNSON,
    role: 'lead',
    joinedAt: '2023-04-01T09:00:00Z',
  },
  MIKE_C_CLOSING_MEMBER: {
    id: TEAM_MEMBERSHIP_IDS.MIKE_C_CLOSING_MEMBER,
    teamId: TEAM_IDS.FUNDING_CLOSING,
    userId: USER_IDS.MIKE_CHEN,
    role: 'member',
    joinedAt: '2023-10-01T09:00:00Z',
  },
  EMILY_N_CLOSING_MEMBER: {
    id: TEAM_MEMBERSHIP_IDS.EMILY_N_CLOSING_MEMBER,
    teamId: TEAM_IDS.FUNDING_CLOSING,
    userId: USER_IDS.EMILY_NGUYEN,
    role: 'member',
    joinedAt: '2024-01-15T09:00:00Z',
  },
  JOHN_CLOSING_MEMBER: {
    id: TEAM_MEMBERSHIP_IDS.JOHN_CLOSING_MEMBER,
    teamId: TEAM_IDS.FUNDING_CLOSING,
    userId: USER_IDS.JOHN_SMITH,
    role: 'member',
    joinedAt: '2024-05-01T09:00:00Z',
  },

  // ==========================================================================
  // OpsMatrix Capital - Servicing Team (3 members)
  // ==========================================================================
  EMILY_D_SERVICING_LEAD: {
    id: TEAM_MEMBERSHIP_IDS.EMILY_D_SERVICING_LEAD,
    teamId: TEAM_IDS.FUNDING_SERVICING,
    userId: USER_IDS.EMILY_DAVIS,
    role: 'lead',
    joinedAt: '2023-03-15T09:00:00Z',
  },
  SARAH_W_SERVICING_MEMBER: {
    id: TEAM_MEMBERSHIP_IDS.SARAH_W_SERVICING_MEMBER,
    teamId: TEAM_IDS.FUNDING_SERVICING,
    userId: USER_IDS.SARAH_WILLIAMS,
    role: 'member',
    joinedAt: '2024-04-01T09:00:00Z',
  },
  DAVID_SERVICING_MEMBER: {
    id: TEAM_MEMBERSHIP_IDS.DAVID_SERVICING_MEMBER,
    teamId: TEAM_IDS.FUNDING_SERVICING,
    userId: USER_IDS.DAVID_PARK,
    role: 'member',
    joinedAt: '2024-06-01T09:00:00Z',
  },

  // ==========================================================================
  // OpsMatrix Capital - Support Team (2 members)
  // ==========================================================================
  EMILY_D_SUPPORT_LEAD: {
    id: TEAM_MEMBERSHIP_IDS.EMILY_D_SUPPORT_LEAD,
    teamId: TEAM_IDS.FUNDING_SUPPORT,
    userId: USER_IDS.EMILY_DAVIS,
    role: 'lead',
    joinedAt: '2023-04-01T09:00:00Z',
  },
  JAMES_SUPPORT_MEMBER: {
    id: TEAM_MEMBERSHIP_IDS.JAMES_SUPPORT_MEMBER,
    teamId: TEAM_IDS.FUNDING_SUPPORT,
    userId: USER_IDS.JAMES_WILSON,
    role: 'member',
    joinedAt: '2024-02-01T09:00:00Z',
  },

  // ==========================================================================
  // OpsMatrix Capital - Draw Inspection Team (2 members)
  // ==========================================================================
  JAMES_DRAW_LEAD: {
    id: TEAM_MEMBERSHIP_IDS.JAMES_DRAW_LEAD,
    teamId: TEAM_IDS.FUNDING_DRAW_INSPECTION,
    userId: USER_IDS.JAMES_WILSON,
    role: 'lead',
    joinedAt: '2023-05-01T09:00:00Z',
  },
  MIKE_C_DRAW_MEMBER: {
    id: TEAM_MEMBERSHIP_IDS.MIKE_C_DRAW_MEMBER,
    teamId: TEAM_IDS.FUNDING_DRAW_INSPECTION,
    userId: USER_IDS.MIKE_CHEN,
    role: 'member',
    joinedAt: '2024-03-01T09:00:00Z',
  },

  // ==========================================================================
  // OpsMatrix Capital - Compliance Team (3 members)
  // ==========================================================================
  MARIA_COMPLIANCE_LEAD: {
    id: TEAM_MEMBERSHIP_IDS.MARIA_COMPLIANCE_LEAD,
    teamId: TEAM_IDS.FUNDING_COMPLIANCE,
    userId: USER_IDS.MARIA_RODRIGUEZ,
    role: 'lead',
    joinedAt: '2023-06-01T09:00:00Z',
  },
  DAVID_COMPLIANCE_MEMBER: {
    id: TEAM_MEMBERSHIP_IDS.DAVID_COMPLIANCE_MEMBER,
    teamId: TEAM_IDS.FUNDING_COMPLIANCE,
    userId: USER_IDS.DAVID_PARK,
    role: 'member',
    joinedAt: '2024-01-01T09:00:00Z',
  },
  ALEX_COMPLIANCE_MEMBER: {
    id: TEAM_MEMBERSHIP_IDS.ALEX_COMPLIANCE_MEMBER,
    teamId: TEAM_IDS.FUNDING_COMPLIANCE,
    userId: USER_IDS.ALEX_KUMAR,
    role: 'member',
    joinedAt: '2024-07-01T09:00:00Z',
  },

  // ==========================================================================
  // OpsMatrix Capital - Executive Team (3 members)
  // ==========================================================================
  RACHEL_EXEC_LEAD: {
    id: TEAM_MEMBERSHIP_IDS.RACHEL_EXEC_LEAD,
    teamId: TEAM_IDS.FUNDING_EXECUTIVE,
    userId: USER_IDS.RACHEL_MARTINEZ,
    role: 'lead',
    joinedAt: '2023-01-01T09:00:00Z',
  },
  OPS_MGR_EXEC_MEMBER: {
    id: TEAM_MEMBERSHIP_IDS.OPS_MGR_EXEC_MEMBER,
    teamId: TEAM_IDS.FUNDING_EXECUTIVE,
    userId: USER_IDS.OPS_MANAGER,
    role: 'member',
    joinedAt: '2023-01-15T09:00:00Z',
  },
  MARIA_EXEC_MEMBER: {
    id: TEAM_MEMBERSHIP_IDS.MARIA_EXEC_MEMBER,
    teamId: TEAM_IDS.FUNDING_EXECUTIVE,
    userId: USER_IDS.MARIA_RODRIGUEZ,
    role: 'member',
    joinedAt: '2024-01-01T09:00:00Z',
  },

  // ==========================================================================
  // 1Sharpe Capital - Originations Team (2 members)
  // ==========================================================================
  SHARPE_ADMIN_ORIG_LEAD: {
    id: TEAM_MEMBERSHIP_IDS.SHARPE_ADMIN_ORIG_LEAD,
    teamId: TEAM_IDS.SHARPE_ORIGINATIONS,
    userId: USER_IDS.SHARPE_ADMIN,
    role: 'lead',
    joinedAt: '2023-05-01T09:00:00Z',
  },
  ANALYST_ORIG_MEMBER: {
    id: TEAM_MEMBERSHIP_IDS.ANALYST_ORIG_MEMBER,
    teamId: TEAM_IDS.SHARPE_ORIGINATIONS,
    userId: USER_IDS.ANALYST,
    role: 'member',
    joinedAt: '2023-07-01T09:00:00Z',
  },

  // ==========================================================================
  // 1Sharpe Capital - Portfolio Team (2 members)
  // ==========================================================================
  PORTFOLIO_MGR_LEAD: {
    id: TEAM_MEMBERSHIP_IDS.PORTFOLIO_MGR_LEAD,
    teamId: TEAM_IDS.SHARPE_PORTFOLIO,
    userId: USER_IDS.PORTFOLIO_MANAGER,
    role: 'lead',
    joinedAt: '2023-05-15T09:00:00Z',
  },
  ANALYST_PORTFOLIO_MEMBER: {
    id: TEAM_MEMBERSHIP_IDS.ANALYST_PORTFOLIO_MEMBER,
    teamId: TEAM_IDS.SHARPE_PORTFOLIO,
    userId: USER_IDS.ANALYST,
    role: 'member',
    joinedAt: '2023-08-01T09:00:00Z',
  },

  // ==========================================================================
  // 1Sharpe Capital - Risk Team (2 members)
  // ==========================================================================
  SHARPE_ADMIN_RISK_LEAD: {
    id: TEAM_MEMBERSHIP_IDS.SHARPE_ADMIN_RISK_LEAD,
    teamId: TEAM_IDS.SHARPE_RISK,
    userId: USER_IDS.SHARPE_ADMIN,
    role: 'lead',
    joinedAt: '2023-06-01T09:00:00Z',
  },
  PORTFOLIO_MGR_RISK_MEMBER: {
    id: TEAM_MEMBERSHIP_IDS.PORTFOLIO_MGR_RISK_MEMBER,
    teamId: TEAM_IDS.SHARPE_RISK,
    userId: USER_IDS.PORTFOLIO_MANAGER,
    role: 'member',
    joinedAt: '2023-09-01T09:00:00Z',
  },

  // ==========================================================================
  // Pacific Bridge Fund - Investment Committee (2 members)
  // ==========================================================================
  FUND_MGR_INVEST_LEAD: {
    id: TEAM_MEMBERSHIP_IDS.FUND_MGR_INVEST_LEAD,
    teamId: TEAM_IDS.PACIFIC_INVESTMENT,
    userId: USER_IDS.FUND_MANAGER,
    role: 'lead',
    joinedAt: '2023-03-01T09:00:00Z',
  },
  PACIFIC_ADMIN_INVEST_MEMBER: {
    id: TEAM_MEMBERSHIP_IDS.PACIFIC_ADMIN_INVEST_MEMBER,
    teamId: TEAM_IDS.PACIFIC_INVESTMENT,
    userId: USER_IDS.PACIFIC_ADMIN,
    role: 'member',
    joinedAt: '2023-04-01T09:00:00Z',
  },

  // ==========================================================================
  // Pacific Bridge Fund - Acquisitions (1 member)
  // ==========================================================================
  FUND_MGR_ACQ_LEAD: {
    id: TEAM_MEMBERSHIP_IDS.FUND_MGR_ACQ_LEAD,
    teamId: TEAM_IDS.PACIFIC_ACQUISITIONS,
    userId: USER_IDS.FUND_MANAGER,
    role: 'lead',
    joinedAt: '2023-03-15T09:00:00Z',
  },

  // ==========================================================================
  // Gulf Coast Family Office - Advisory (1 member)
  // ==========================================================================
  PRINCIPAL_ADVISORY_LEAD: {
    id: TEAM_MEMBERSHIP_IDS.PRINCIPAL_ADVISORY_LEAD,
    teamId: TEAM_IDS.GULF_ADVISORY,
    userId: USER_IDS.PRINCIPAL,
    role: 'lead',
    joinedAt: '2023-02-01T09:00:00Z',
  },

  // ==========================================================================
  // Gulf Coast Family Office - Investments (1 member)
  // ==========================================================================
  PRINCIPAL_INVESTMENTS_LEAD: {
    id: TEAM_MEMBERSHIP_IDS.PRINCIPAL_INVESTMENTS_LEAD,
    teamId: TEAM_IDS.GULF_INVESTMENTS,
    userId: USER_IDS.PRINCIPAL,
    role: 'lead',
    joinedAt: '2023-02-15T09:00:00Z',
  },
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

export function getAllTeams(workspace?: string): TeamSeedData[] {
  const teams = Object.values(TEAMS);
  if (!workspace) return teams;
  return teams.map((t) => ({ ...t, name: `${t.name} [${workspace}]` }));
}

export function getAllTeamMemberships(): TeamMembershipSeedData[] {
  return Object.values(TEAM_MEMBERSHIPS);
}

export function getTeamsByBusiness(businessId: string): TeamSeedData[] {
  return Object.values(TEAMS).filter((t) => t.businessId === businessId);
}

export function getMembershipsByTeam(teamId: string): TeamMembershipSeedData[] {
  return Object.values(TEAM_MEMBERSHIPS).filter((m) => m.teamId === teamId);
}

export function getMembershipsByUser(userId: string): TeamMembershipSeedData[] {
  return Object.values(TEAM_MEMBERSHIPS).filter((m) => m.userId === userId);
}

export function getTeamLeads(): TeamMembershipSeedData[] {
  return Object.values(TEAM_MEMBERSHIPS).filter((m) => m.role === 'lead');
}

export function getActiveTeams(): TeamSeedData[] {
  return Object.values(TEAMS).filter((t) => t.isActive);
}
